import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { postMessage } from 'utils/postMessage';
import hash from 'hash-it';

import {
  configWidget,
  reloadTranslatedWidgetConfig,
  setCsatFeedback,
  setCsatScore,
  setCsatVisibility,
  setHandoffStatus,
  setSelectedArticle,
  setStepSent,
  setStepContextVariables,
  setIsCsatDataSubmitted,
  setCustomParams,
  setCustomConfigParams,
  resetReduxState,
  setIsBuilderPreview,
  setWorkflowVersion,
  restartConversation,
  setLogRocketInitialized,
  setHandoffIntegration,
  setSelectedIntentIndex,
  resetPreviewWidgetConfig,
  setCsatDetailVisibility,
  setCsatResolve,
  resetFeedback,
  setIsCsatEndTransition,
  setIsCsatSurveyFinished,
  setDidUserCloseProactiveChat,
  setIntentTitle,
  setIsModelTraining,
  setTriggerEventStatus,
  setTriggerEventExpectedContextVariables,
  setTriggerEventExpectedEventId,
  setTriggerEventExpectedEventName,
  setTriggerEventPayloadContextVariables,
  setTriggerEventPayloadEventName,
  cleanTriggerEvent,
  setParentUrl,
  setStepFileNames,
  setIsSolveConfigPreview,
  setInitialIntentId,
  setCsatType,
  setDidSubmit,
  setInitialQuery,
  sendTrackingEvent,
  updateCustomConfigParams,
  setUserInteractions,
  setLastProactiveTriggerId,
} from 'actions/workflows/workflows';
import {
  closeWidget,
  navigateTo,
  openWidget,
  setIsWidgetVisible,
} from 'actions/main';
import {
  getTranslatedCsatText,
  initIntentConversation,
  getIntentsBySelectedTag,
  startNewIntentSelection,
  updateIntentConversation,
  setFreeFormQuery,
  setConversationId,
  getKnowledgeRetrievalStream,
  sendConversationFeedback,
  getPersistedLiveChatComponents,
  searchKnowledge,
  sendFreeFormQuery,
  getAutoflowStream,
} from 'actions/workflows-v2/workflowsV2';
import {
  sendAgentReply,
  sendUserReply,
  updateForethoughtLiveChatHelpdeskConversationId,
} from 'actions/live-chat';

import type {
  WorkflowConfigArgs,
  ArticleComponent,
  WidgetComponent,
  ContextVariables,
  SelectIntentComponent,
  CsatFormData,
  CsatConfig,
  HandoffIntegrations,
  CsatTranslatedText,
  TriggerEventStepContextVariable,
  UserMessageComponent,
  IntentConversationResponse,
  WidgetMode,
  ConversationNotFoundError,
  StreamingResponse,
  CsatStates,
  SipCallState,
  NewIrArticleComponent,
  AttemptedToFetchRedactedConversationError,
  ForethoughtLiveChatHandoffComponent,
  SuncoLiveChatComponent,
  ExternalEventAutomation,
  KnowledgeSearchResponse,
  FilteredWorkflowConfig,
  ComponentTypes,
  ProactiveTrigger,
  BrowserAgentComponent,
} from '../../api/types';
import {
  isFormComponent,
  isWorkflowTagSelectorComponent,
  isZendeskAttachmentUploadStepComponent,
  isTextMessageComponent,
  isConversationNotFoundError,
  isAttemptedToFetchRedactedConversationError,
  isForethoughtLiveChatHandoffComponent,
  isBadRequestError,
  isTooManyRequestsError,
  isEventMessageComponent,
  isUserMessageComponent,
  isSuncoLiveChatComponent,
  isPreviewLogStreamingResponse,
  isOauthSignInComponent,
  isButtonStepWithHideFreeFormQueryField,
  isNonJsonResponse520Error,
} from '../../api/types';
import type { RootState } from '../root';
import {
  filterWorkflowConfig,
  getPrepopulatedComponents,
  getSelectIntentStep,
  getWorkflowTagSelectorMessageStep,
  getWorkflowTagSelectorStep,
  isValidConversationStatusForCsat,
} from './utils';

import { resetIntentConversation } from 'actions/workflows-v2/workflowsV2';
import { noUserActionsLeft } from '../../utils/helpers';
import initialState from './initialState';
import {
  autonomousAgentQuery,
  initAutonomousAgentConversation,
} from 'actions/workflows/v3';
import { disablePersistence } from 'persistence/persistenceBetweenTabs';
import { ARTICLE_PAGE } from 'constants/pageConsts';
import {
  ARTIFICIAL_DELAY_IN_MILLISECONDS,
  PREFIX_TO_SKIP_RENDERING_WIDGET_COMPONENT,
} from 'components/App/consts';
import {
  getSystemClockDelta,
  isObjectEmpty,
  parseDateToUtcTimestamp,
} from 'utils/utils';
import { HIGH_RATING_REASONS_STAGE } from 'components/Csat/constants';
import { isForethoughtDisabledByLocalStorage } from 'utils/disableForethoughtWidget';
import { defaultPersistenceDurationMinutes } from 'persistence/persistence';
import type { PersistedState } from 'persistence/types';
import {
  selectAreAgentTypingDotsVisible,
  selectLiveChatWidgetComponentExists,
} from 'reducers/live-chat';
import getMessageBubblePositions from 'utils/getMessageBubblePositions';
import { invisibleComponentTypes } from 'utils/invisibleComponentTypes';
import type { CustomFetchErrorObject } from 'api/customFetch';
import { isCustomFetchErrorObject } from 'api/customFetch';
import { networkErrorMessages } from 'api/utils';
import {
  selectIsAutonomousAgent,
  selectIsDraft,
  selectLaunchStyle,
} from 'reducers/config';
import { validate } from 'uuid';
import { postForethoughtWidgetPreviewLogMessage } from 'actions/workflows-v2/utils';
import { getOverridableConfigValue } from 'utils/getOverridableConfigValue';
import type { RoutingAttributes } from 'components/salesforce-messaging-one-chat/types';
import { LOADING_STATIC_TEXT } from 'components/BrowserAgentLoading/constants';

export type HandoffStatus =
  | 'complete'
  | 'failure'
  | 'not_started'
  | 'started'
  | 'success';

export type TriggerEventStatus =
  | 'not_started'
  | 'expecting_response'
  | 'in_progress'
  | 'complete';

export interface WorkflowsState {
  actionToExecAfterPrivacyConsented:
    | 'intentOptionClicked'
    | 'initialIntentTriggered'
    | 'handleFreeFormInput'
    | null;
  consentedPrivacyPolicyHash: number | null;
  conversationId: string;
  conversationIdUsedInPreviousSession: string;
  conversationStatus: 'initial' | 'start' | 'success' | 'failure';
  csatConfig: CsatConfig;
  csatFormData: CsatFormData;
  csatStates: CsatStates;
  csatTranslatedText: CsatTranslatedText | Record<string, never> | null;
  customConfigParams?: ContextVariables;
  customParams: ContextVariables;
  didProactiveBubblesShownTrackingEventGetSent: boolean;
  didUserCloseProactiveChat: boolean;
  didUserRestartConversation: boolean;
  dismissableNotification: string;
  enabled_feature_flags: string[];
  error:
    | string
    | ConversationNotFoundError
    | AttemptedToFetchRedactedConversationError
    | CustomFetchErrorObject
    | null;
  externalEventAutomations: ExternalEventAutomation[] | null;
  floatingBarSearchLoading: boolean;
  floatingBarSearchResult?: KnowledgeSearchResponse;
  freeFormQuery: string;
  handoffFailureMessage: string | null;
  handoffIntegration: HandoffIntegrations | null;
  handoffStatus: HandoffStatus;
  initialIntentId: string;
  initialQuery: string;
  intentId: string;
  intentTitle: string;
  interactiveEmailDeflectionFeedback: 'deflected' | 'not-deflected' | null;
  isAutonomousAgentWorkflowPreview: boolean;
  isBrowserAgentInProgress: boolean;
  isBuilderPreview: boolean;
  isFirstConversation?: boolean;
  isFloatingBarFocused: boolean;
  isFreshdeskFileSent: boolean;
  isModelTraining: boolean;
  isScrollAtTheBottom: boolean;
  isSolveConfigPreview: boolean;
  isUploadingAttachment: boolean;
  knowledgeSearchQuery: string;
  lastProactiveTriggerId: string | null;
  loading: boolean;
  messageDelays: {
    delay: number;
    shownAtInMilliseconds: number;
    status: 'not delayed' | 'delaying' | 'delayed';
  }[];
  modifiedDate: string | null;
  nextStepId: string | null;
  noUserActionsLeft: boolean;
  numOfUserActions: number;
  parentUrl: string;
  persistedLiveChatComponents: WidgetComponent[] | null;
  previousIntentIds: Array<string>;
  proactiveTriggers?: ProactiveTrigger[];
  quickFeedback: {
    mobileClickedStepIndex: number | null;
    steps: Record<
      number, // step index
      {
        additionalFeedback: string;
        isSent: boolean;
        isUseful: boolean;
      }
    >;
  };
  screenReaderNotifications: string[];
  selectedArticle?: ArticleComponent | NewIrArticleComponent;
  selectedIntentIndex: number | null;
  sessionId: number | null;
  shouldFocusReceivedMessage: boolean;
  shouldFocusTextInput: boolean;
  shouldInitLogRocket: boolean;
  sipCallState: SipCallState;
  streaming: boolean;
  systemTimeDelta: number;
  touchedFormFields: string[];
  transcriptLength: number | null;
  triggerEvent: {
    triggerEventExpectedContextVariables: TriggerEventStepContextVariable[];
    triggerEventExpectedEventId: string;
    triggerEventExpectedEventName: string;
    triggerEventPayload: {
      contextVariables: ContextVariables;
      eventName: string;
    };
    triggerEventStatus: TriggerEventStatus;
  };
  userInteractions?: number | null;
  widget_enabled: boolean;
  widgetComponents: Array<WidgetComponent>;
  widgetSearchLoading: boolean;
  widgetSearchResult?: KnowledgeSearchResponse;
  windowUpdatedContextVariables: ContextVariables;
  workflowsConfig: FilteredWorkflowConfig | null;
  workflowVersion: string;
}

const handleCsatLogic = (noActionsLeft: boolean, state: WorkflowsState) => {
  if (
    state.workflowsConfig?.csat_survey_config?.should_only_show_on_close ||
    (state.enabled_feature_flags || defaultFeatureFlags).includes(
      'csat_config_revamp',
    )
  ) {
    return;
  }

  if (noActionsLeft) {
    state.noUserActionsLeft = true;
    const showCsatSurvey =
      state.workflowsConfig?.csat_survey_config?.is_enabled &&
      state.numOfUserActions >=
        state?.workflowsConfig?.csat_survey_config?.num_of_steps_required &&
      state.conversationId &&
      !state.csatConfig?.isCsatSurveyFinished &&
      !state.isBuilderPreview;
    if (showCsatSurvey) {
      const newComponents = getPrepopulatedComponents([
        {
          component_fields: {
            forethought_text: {
              checkbox_no: '',
              checkbox_placeholder: '',
              checkbox_yes: '',
              dropdown_back: '',
              email_error: '',
              never_mind: '',
              number_error: '',
              phone_error: '',
              required_error: '',
              required_label: '',
              short_text_alphanumeric_error: '',
              short_text_equal_error: '',
              short_text_max_error: '',
              short_text_min_error: '',
              submit_button: '',
            },
            form_fields: [],
            prompt: '',
          },
          component_type: 'csat_survey',
          contextVariables: {},
        },
      ]);
      state.widgetComponents = [...state.widgetComponents, ...newComponents];
    }
  }
};

function handleWidgetComponents(
  widgetComponents: WidgetComponent[] | undefined,
  state: WorkflowsState,
  shouldHandleCsatLogic: boolean,
) {
  if (widgetComponents) {
    const newComponents = getPrepopulatedComponents(widgetComponents);
    state.widgetComponents = [...state.widgetComponents, ...newComponents];
    const currentWidgetComponent = newComponents[newComponents.length - 1];
    if (currentWidgetComponent !== undefined && shouldHandleCsatLogic) {
      const noActionsLeft = noUserActionsLeft(currentWidgetComponent);
      handleCsatLogic(noActionsLeft, state);
    }
  }
}

const handleRedaction = (state: WorkflowsState, redactedText?: string) => {
  const isRedactSrpWordsEnabled =
    state.enabled_feature_flags.includes('redact_srp_words');
  const lastWidgetComponent =
    state.widgetComponents[state.widgetComponents.length - 1];
  if (isRedactSrpWordsEnabled && lastWidgetComponent && redactedText) {
    lastWidgetComponent.query = redactedText;
  }
};

const receiveUpdateConversationResponse = (
  state: WorkflowsState,
  {
    payload,
  }: {
    payload: IntentConversationResponse;
  },
) => {
  state.loading = false;
  state.error = null;
  state.dismissableNotification = '';
  state.numOfUserActions = state.numOfUserActions + 1;
  const widgetComponents = payload?.widget_components;
  state.transcriptLength = payload?.transcript_length;
  state.nextStepId = payload?.next_step_id;
  state.windowUpdatedContextVariables = {};
  state.csatStates.isPromptDisabledForMinimize = !payload.is_csat_promptable;
  state.userInteractions = payload.user_interactions;
  if (payload?.modified_date) {
    state.modifiedDate = payload.modified_date;
    state.systemTimeDelta = getSystemClockDelta(payload.modified_date);
  }
  if (payload?.conversation_id) {
    state.conversationId = payload.conversation_id;
  }
  handleRedaction(state, payload.redacted_text);
  handleWidgetComponents(widgetComponents, state, true);
  if (payload.previous_intent_ids?.length) {
    state.previousIntentIds = payload.previous_intent_ids;
  }
  if (payload.intent_id) {
    state.intentId = payload.intent_id;
    if (!state.previousIntentIds.includes(payload.intent_id)) {
      state.previousIntentIds.push(payload.intent_id);
    }
  }
};

function insertLiveChatWidgetComponents(
  state: WorkflowsState,
  liveChatComponents: WidgetComponent[],
) {
  const filterComponents = (
    component: WidgetComponent,
    isInAgentConvo: boolean,
  ) => {
    if (isTextMessageComponent(component)) {
      return component.isInAgentConvo === isInAgentConvo;
    }
    return (
      (isEventMessageComponent(component) ||
        isUserMessageComponent(component)) === isInAgentConvo
    );
  };

  const currentLiveChatComponents = state.widgetComponents.filter(component =>
    filterComponents(component, true),
  );

  if (liveChatComponents.length > currentLiveChatComponents.length) {
    const currentWidgetComponents = state.widgetComponents.filter(component =>
      filterComponents(component, false),
    );

    state.widgetComponents = [
      ...currentWidgetComponents,
      ...liveChatComponents,
    ];
  }
}

export const workflowsSlice = createSlice({
  extraReducers: builder => {
    builder.addCase(reloadTranslatedWidgetConfig.pending, state => {
      state.error = null;
      state.dismissableNotification = '';
    });
    builder.addCase(reloadTranslatedWidgetConfig.rejected, (state, action) => {
      if (
        !!state.workflowsConfig &&
        isCustomFetchErrorObject(action.payload) &&
        networkErrorMessages.includes(action.payload.message)
      ) {
        /**
         * do nothing because workflow config has already loaded (default value is null)
         * and browser most likely canceled the request due to a page refresh / redirect
         * mid request.
         */
      } else if (
        isCustomFetchErrorObject(action.payload) &&
        isNonJsonResponse520Error(action.payload) &&
        !!state.workflowsConfig &&
        !!state.conversationId
      ) {
        /**
         * Do nothing when we already have a workflow config and we get a 520 error
         * from Cloudflare. This is rarely seen.
         */
      } else if (
        isAttemptedToFetchRedactedConversationError(action.payload) ||
        isCustomFetchErrorObject(action.payload)
      ) {
        state.error = action.payload;
      } else {
        state.error = action.error.message || null;
      }
    });
    builder.addCase(
      reloadTranslatedWidgetConfig.fulfilled,
      (state, { payload }) => {
        state.loading = false;
        if (payload && state.workflowsConfig != null) {
          // payload for these values can be undefined
          if (payload.forethought_text !== undefined) {
            state.workflowsConfig.forethought_text = payload.forethought_text;
          }
          if (payload.top_n_intents !== undefined) {
            state.workflowsConfig.top_n_intents = payload.top_n_intents;
          }

          if (payload.greeting_line !== undefined) {
            state.workflowsConfig.greeting_line = payload.greeting_line;
          }

          if (payload.csat_config !== undefined) {
            state.workflowsConfig.csat_config = payload.csat_config;
          }

          if (payload.quick_feedback_config !== undefined) {
            state.workflowsConfig.quick_feedback_config =
              payload.quick_feedback_config;
          }
        }
        if (payload?.conversation_id) {
          state.conversationId = payload.conversation_id;
        }
      },
    );
    builder.addCase(configWidget.pending, state => {
      // Make sure widget doesn't get stuck in an ampty state after an error
      // gets thrown by `initIntentConversation()` and after user refreshes the
      // page.
      // Setting `state.conversationId` to '' forces `configWidget.fulfilled`
      // reducer to think that the conversation is new.
      if (state.error && state.widgetComponents.length === 0) {
        state.conversationId = '';
      }

      state.error = null;
      state.dismissableNotification = '';
    });
    builder.addCase(configWidget.rejected, (state, action) => {
      if (
        !!state.workflowsConfig &&
        isCustomFetchErrorObject(action.payload) &&
        networkErrorMessages.includes(action.payload.message)
      ) {
        /**
         * do nothing because workflow config has already loaded (default value is null)
         * and browser most likely canceled the request due to a page refresh / redirect
         * mid request.
         */
      } else if (
        isCustomFetchErrorObject(action.payload) &&
        isNonJsonResponse520Error(action.payload) &&
        !!state.workflowsConfig &&
        !!state.conversationId
      ) {
        /**
         * Do nothing when we already have a workflow config and we get a 520 error
         * from Cloudflare. This is rarely seen.
         */
      } else if (
        isAttemptedToFetchRedactedConversationError(action.payload) ||
        isCustomFetchErrorObject(action.payload)
      ) {
        state.error = action.payload;
      } else {
        state.error = action.error.message || null;
      }
    });
    builder.addCase(configWidget.fulfilled, (state, { payload }) => {
      const workflowTagSelectorMessage = getWorkflowTagSelectorMessageStep(
        (state.customConfigParams?.greeting_message as string | undefined) ||
          payload.greeting_line ||
          '',
      );
      const workflowParams = payload.workflow_params ?? {};

      const workflowTags = payload.workflow_tags ?? [];

      const workflowTagSelector = getWorkflowTagSelectorStep(
        workflowTags,
        payload.forethought_text?.tag_selection_button_greeting_message || '',
      );

      const selectIntentStep = getSelectIntentStep(
        (state.customConfigParams?.greeting_message as string | undefined) ||
          payload.greeting_line ||
          '',
      );
      const isNewConversation = !state.conversationId;

      const includesWorkflowTagAttribute =
        Object.keys(workflowParams).includes('workflow_tag');

      const isWorkflowTagSelectionMode =
        payload.tag_selection_enabled &&
        workflowTags.length > 0 &&
        !includesWorkflowTagAttribute;

      const shouldDisplayWorkflowTagSelector =
        isWorkflowTagSelectionMode && isNewConversation && !payload.intentId;

      const isPrefilled =
        payload?.greeting_line && !payload.intentId && isNewConversation;
      const initialQuery = state.initialQuery || payload.initialQuery;
      const isInitialQuery = !!initialQuery && isNewConversation;

      state.loading = false;
      state.error = null;
      state.dismissableNotification = '';
      state.numOfUserActions = 0;
      if (payload) {
        const {
          conversation_id: conversationId,
          initial_widget_components: initialWidgetComponents,
        } = payload;
        state.workflowsConfig = filterWorkflowConfig(payload);
        state.enabled_feature_flags = payload.enabled_feature_flags;

        // move conversation_id out to persist:
        state.conversationId = conversationId;

        // move widget_enabled out to persist
        state.widget_enabled = payload.widget_enabled;

        const isPrivacyPolicyChanged =
          hash(payload.privacy_consent_config?.privacy_policy || '') !==
          state.consentedPrivacyPolicyHash;

        if (isPrivacyPolicyChanged) {
          state.consentedPrivacyPolicyHash = null;
        }

        state.customParams = payload.workflow_params ?? {};
        state.customConfigParams = payload.config_params;
        if (initialWidgetComponents && isNewConversation) {
          state.widgetComponents = initialWidgetComponents;
        } else if (payload.isAutonomousAgent) {
          state.widgetComponents = [workflowTagSelectorMessage];
        } else if (shouldDisplayWorkflowTagSelector) {
          state.widgetComponents = [
            workflowTagSelectorMessage,
            workflowTagSelector,
          ];
        } else if (isInitialQuery) {
          state.widgetComponents = [
            {
              component_fields: { message: initialQuery },
              component_type: 'user_message',
            },
          ];
        } else if (isPrefilled) {
          state.widgetComponents = [selectIntentStep];
        }
        state.externalEventAutomations = payload.external_event_automations;
        state.proactiveTriggers = payload.proactive_triggers;
      }
    });
    // Init
    builder.addCase(initIntentConversation.pending, (state, action) => {
      state.conversationStatus = 'start';
      state.loading = true;
      state.error = null;
      state.dismissableNotification = '';
      const intentName =
        state.workflowsConfig?.top_n_intents?.find(
          (_, index) => index === state.selectedIntentIndex,
        )?.intent_name || '';
      const selectIntentStep = getSelectIntentStep('', true, intentName);

      const isTriggeredByProactiveChat = action.meta.arg.isProactive;

      const isWorkflowTagSelector =
        state.widgetComponents[1] &&
        isWorkflowTagSelectorComponent(state.widgetComponents[1]);

      if (isWorkflowTagSelector && isTriggeredByProactiveChat) {
        state.widgetComponents = [selectIntentStep];
      }
    });
    builder.addCase(initIntentConversation.rejected, (state, action) => {
      state.conversationStatus = 'failure';
      state.loading = false;
      state.widgetComponents = [];
      state.conversationId = '';
      const payload = action.payload;
      if (
        isConversationNotFoundError(payload) ||
        isCustomFetchErrorObject(payload)
      ) {
        state.error = payload;
      } else {
        state.error = action.error.message || null;
      }
    });
    builder.addCase(initIntentConversation.fulfilled, (state, { payload }) => {
      state.conversationStatus = 'success';
      handleRedaction(state, payload.redacted_text);
      const intentId = payload.intent_id;
      const widgetComponents = payload.widget_components
        ? getPrepopulatedComponents(payload.widget_components)
        : [];

      state.loading = false;
      state.error = null;
      state.dismissableNotification = '';
      state.conversationId = payload.conversation_id;
      state.transcriptLength = payload?.transcript_length;
      state.nextStepId = payload?.next_step_id;
      state.numOfUserActions = 0;
      state.noUserActionsLeft = false;
      state.windowUpdatedContextVariables = {};
      state.userInteractions = payload.user_interactions;

      if (payload.previous_intent_ids?.length) {
        state.previousIntentIds = payload.previous_intent_ids;
      }
      if (payload.modified_date) {
        state.modifiedDate = payload.modified_date;
        state.systemTimeDelta = getSystemClockDelta(payload.modified_date);
      }
      /**
       * First conversation can be found when
       * 1. isFirstConversation is undefined
       * 2. state.intentId is empty string
       * 3. intent_id from incoming payload has value
       */
      if (state.isFirstConversation === undefined) {
        state.isFirstConversation =
          state.intentId === '' && !!payload.intent_id;
      } else {
        state.isFirstConversation = false;
      }
      const isWorkflowTagSelector =
        state.widgetComponents[1] &&
        isWorkflowTagSelectorComponent(state.widgetComponents[1]);
      if (state.initialIntentId === intentId) {
        state.intentId = intentId;
        if (state.isFirstConversation && isWorkflowTagSelector) {
          // Ignore workflow trigger if tag needs to be chosen
          return;
        } else if (
          state.isFirstConversation &&
          !isWorkflowTagSelector &&
          state.widgetComponents[0]?.component_type !==
            'interactive_email_inquiry'
        ) {
          // Workflow trigger is used - remove greeting line
          state.widgetComponents = widgetComponents;
        } else {
          if (
            !state.isFirstConversation &&
            state.workflowsConfig?.initial_widget_components?.length &&
            state.widgetComponents[0]?.component_type ===
              'restart_conversation' &&
            state.widgetComponents.length === 1
          ) {
            // on first load we set initial_widget_components from configWidget
            // on restart add the initial widget components here to keep the flow the same
            // if the conversation is restarted from nevermind click, there is still more
            // than 1 widget components. Do not add initial_widget_components in this case
            state.widgetComponents.push(
              ...[
                ...state.workflowsConfig.initial_widget_components,
                ...widgetComponents,
              ],
            );
          } else {
            state.widgetComponents.push(...widgetComponents);
          }
        }

        if (!state.previousIntentIds.includes(intentId)) {
          state.previousIntentIds.push(intentId);
        }
      } else {
        state.widgetComponents = [
          ...state.widgetComponents,
          ...widgetComponents,
        ];
      }

      /**
       * if we're selecting intent from a option, initialIntentId would stay empty,
       */
      if (intentId && state.initialIntentId === '') {
        state.intentId = intentId;
      }

      if (state.initialQuery && intentId && state.isFirstConversation) {
        // set intentId state if initialized from initial-query
        state.intentId = intentId;
        if (!state.previousIntentIds.includes(intentId)) {
          state.previousIntentIds.push(intentId);
        }
      }
    });
    builder.addCase(
      initAutonomousAgentConversation.fulfilled,
      (state, { payload }) => {
        state.loading = false;
        state.intentId = payload.intent_id;
        state.isAutonomousAgentWorkflowPreview = payload.is_workflow_preview;

        state.widgetComponents = [
          ...state.widgetComponents,
          ...payload.widget_components,
        ];

        postMessage({
          agentActions: payload.internal_output,
          agentResponse: (payload.widget_components[0] as UserMessageComponent)
            ?.component_fields.message as string,
          event: 'autonomousAgentConversationUpdated',
          intentId: payload.intent_id,
          intentTitle: payload.intent_title,
          policy: payload.policy,
        });
      },
    );
    builder.addCase(initAutonomousAgentConversation.pending, state => {
      state.loading = true;
    });
    builder.addCase(autonomousAgentQuery.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.intentId = payload.intent_id;
      state.isAutonomousAgentWorkflowPreview = payload.is_workflow_preview;

      state.widgetComponents = [
        ...state.widgetComponents,
        ...payload.widget_components,
      ];

      postMessage({
        agentActions: payload.internal_output,
        agentResponse: (payload.widget_components[0] as UserMessageComponent)
          ?.component_fields.message as string,
        event: 'autonomousAgentConversationUpdated',
        intentId: payload.intent_id,
        intentTitle: payload.intent_title,
        policy: payload.policy,
      });
    });
    builder.addCase(autonomousAgentQuery.pending, state => {
      state.loading = true;
    });
    // Update
    builder.addCase(updateIntentConversation.pending, state => {
      state.loading = true;
      state.error = null;
      state.dismissableNotification = '';
      // made the call to resume, set to null
      if (
        state.csatStates.actionToExecuteAfterCsatDismiss ===
        'resume-from-trigger-point'
      ) {
        state.csatStates.actionToExecuteAfterCsatDismiss = null;
      }
    });
    builder.addCase(updateIntentConversation.rejected, (state, action) => {
      const payload = action.payload;
      if (
        (isBadRequestError(payload) || isTooManyRequestsError(payload)) &&
        'isInteractiveEmail' in payload &&
        payload.isInteractiveEmail
      ) {
        state.widgetComponents =
          state.workflowsConfig?.initial_widget_components ?? [];
      } else {
        state.loading = false;
        if (
          isConversationNotFoundError(payload) ||
          isCustomFetchErrorObject(payload)
        ) {
          state.error = payload;
        } else {
          state.error = action.error.message || null;
        }
      }
    });
    builder.addCase(
      updateIntentConversation.fulfilled,
      receiveUpdateConversationResponse,
    );
    builder.addCase(setCsatFeedback, (state, action) => {
      state.csatFormData.csatFeedback = action.payload;
    });
    builder.addCase(setIsCsatDataSubmitted, (state, action) => {
      state.csatFormData.isCsatDataSubmitted = action.payload;
    });
    builder.addCase(setIsCsatEndTransition, (state, action) => {
      state.csatConfig.isCsatEndTransition = action.payload;
    });
    builder.addCase(setIsCsatSurveyFinished, (state, action) => {
      state.csatConfig.isCsatSurveyFinished = action.payload;
    });
    builder.addCase(setCsatResolve, (state, action) => {
      state.csatFormData.csatResolve = action.payload;
    });
    builder.addCase(setCsatDetailVisibility, (state, action) => {
      state.csatConfig.isCsatDetailVisible = action.payload;
    });
    builder.addCase(resetFeedback, state => {
      state.csatFormData = {
        csatFeedback: [],
        csatResolve: undefined,
        csatScore: state.csatFormData.csatScore,
        isCsatDataSubmitted: false,
      };
      state.csatConfig = {
        ...state.csatConfig,
        isCsatDetailVisible: false,
        isCsatEndTransition: false,
      };
    });
    builder.addCase(setCsatScore, (state, action) => {
      state.csatFormData.csatScore = action.payload;
    });
    builder.addCase(setCsatVisibility, (state, action) => {
      state.csatConfig.isCsatVisible = action.payload;
    });
    builder.addCase(setCsatType, (state, action) => {
      state.csatConfig.csatType = action.payload;
    });
    builder.addCase(setDidSubmit, (state, action) => {
      state.csatConfig.didSubmit = action.payload;
    });
    builder.addCase(setCustomParams, (state, action) => {
      state.customParams = action.payload;
    });
    builder.addCase(setIntentTitle, (state, action) => {
      state.intentTitle = action.payload;
    });
    builder.addCase(setIsModelTraining, (state, action) => {
      state.isModelTraining = action.payload;
    });
    builder.addCase(setCustomConfigParams, (state, action) => {
      state.customConfigParams = action.payload;
    });
    builder.addCase(updateCustomConfigParams, (state, action) => {
      if (typeof action.payload !== 'object' || action.payload === null) {
        return;
      }

      const transformedObj: ContextVariables = {};

      for (const [key, value] of Object.entries(action.payload)) {
        // Only process keys that start with "config-ft-"
        if (key.startsWith('config-ft-')) {
          const newKey = key
            .replace(/^config-ft-/, '') // remove prefix
            .replace(/-/g, '_'); // convert dashes to underscores

          transformedObj[newKey] = value;
        } else {
          transformedObj[key] = value;
        }
      }

      state.customConfigParams = {
        ...state.customConfigParams,
        ...transformedObj,
      };
    });
    builder.addCase(setHandoffIntegration, (state, action) => {
      state.handoffIntegration = action.payload;
    });
    builder.addCase(setHandoffStatus, (state, action) => {
      state.handoffStatus = action.payload;
    });
    builder.addCase(setTriggerEventStatus, (state, action) => {
      state.triggerEvent.triggerEventStatus = action.payload;
    });
    builder.addCase(setTriggerEventPayloadContextVariables, (state, action) => {
      // only update state if we are expecting it
      if (state.triggerEvent.triggerEventStatus === 'in_progress') {
        state.triggerEvent.triggerEventPayload.contextVariables =
          action.payload;
      }
    });
    builder.addCase(setTriggerEventPayloadEventName, (state, action) => {
      // only update state if we are expecting it
      if (state.triggerEvent.triggerEventStatus === 'in_progress') {
        state.triggerEvent.triggerEventPayload.eventName = action.payload;
      }
    });
    builder.addCase(
      setTriggerEventExpectedContextVariables,
      (state, action) => {
        state.triggerEvent.triggerEventExpectedContextVariables =
          action.payload;
      },
    );
    builder.addCase(setTriggerEventExpectedEventName, (state, action) => {
      state.triggerEvent.triggerEventExpectedEventName = action.payload;
    });
    builder.addCase(setTriggerEventExpectedEventId, (state, action) => {
      state.triggerEvent.triggerEventExpectedEventId = action.payload;
    });
    builder.addCase(cleanTriggerEvent, state => {
      state.triggerEvent = { ...initialState.triggerEvent };
    });
    builder.addCase(setLastProactiveTriggerId, (state, action) => {
      state.lastProactiveTriggerId = action.payload;
    });

    builder.addCase(setSelectedArticle, (state, action) => {
      state.selectedArticle = action.payload;
    });
    builder.addCase(setStepSent, (state, action) => {
      const index =
        action.payload.stepIndex || state.widgetComponents.length - 1;

      if (state.widgetComponents[index] !== undefined) {
        // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
        state.widgetComponents[index]!.isSent = true;
      }
      if (action.payload.query) {
        (state.widgetComponents[index] as SelectIntentComponent).query =
          action.payload.query;
      }

      state.touchedFormFields = [];
    });

    builder.addCase(setStepFileNames, (state, action) => {
      const stepCopmonent = state.widgetComponents[action.payload.stepIndex];

      if (
        stepCopmonent === undefined ||
        !isZendeskAttachmentUploadStepComponent(stepCopmonent)
      ) {
        return;
      }

      stepCopmonent.isSent = true;
      if (action.payload.fileNames) {
        stepCopmonent.file_names = action.payload.fileNames;
      }
    });

    builder.addCase(setStepContextVariables, (state, action) => {
      const payload = action.payload;
      const index = payload.index;
      if (state.widgetComponents[index] !== undefined) {
        // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
        state.widgetComponents[index]!.contextVariables =
          payload.contextVariables;
      }
    });

    builder.addCase(setIsBuilderPreview, (state, action) => {
      const payload = action.payload;
      state.isBuilderPreview = payload;
    });

    builder.addCase(setUserInteractions, (state, action) => {
      state.userInteractions = action.payload;
    });

    builder.addCase(setIsSolveConfigPreview, (state, action) => {
      const payload = action.payload;
      state.isSolveConfigPreview = payload;
    });

    builder.addCase(setInitialIntentId, (state, action) => {
      const payload = action.payload;
      state.initialIntentId = payload;
    });

    builder.addCase(setInitialQuery, (state, action) => {
      state.initialQuery = action.payload;
    });

    builder.addCase(setWorkflowVersion, (state, action) => {
      const payload = action.payload;
      state.workflowVersion = payload;
    });

    builder.addCase(setParentUrl, (state, action) => {
      const payload = action.payload;
      state.parentUrl = payload;
    });

    builder.addCase(setSelectedIntentIndex, (state, { payload }) => {
      state.selectedIntentIndex = payload;
    });

    builder.addCase(resetReduxState, state => {
      return {
        ...initialState,

        conversationIdUsedInPreviousSession:
          state.conversationIdUsedInPreviousSession,
        customConfigParams: state.customConfigParams,
        customParams: state.customParams,
        enabled_feature_flags: state.enabled_feature_flags,
        handoffIntegration: state.handoffIntegration,
        initialIntentId: state.initialIntentId,
        isSolveConfigPreview: state.isSolveConfigPreview,
        persistedLiveChatComponents: null,
        widget_enabled: state.widget_enabled,
        workflowsConfig: state.workflowsConfig,

        ...(state.enabled_feature_flags.includes(
          'remember_privacy_policy_consent',
        )
          ? {
              consentedPrivacyPolicyHash: state.consentedPrivacyPolicyHash,
            }
          : {}),
      };
    });

    builder.addCase(restartConversation.pending, state => {
      state.conversationStatus = 'start';
      state.widgetComponents = [];
      state.loading = true;
      state.error = null;
      state.dismissableNotification = '';
      state.triggerEvent = { ...initialState.triggerEvent };
      state.quickFeedback = initialState.quickFeedback;
    });
    builder.addCase(restartConversation.rejected, (state, action) => {
      state.conversationStatus = 'failure';
      state.loading = false;
      state.widgetComponents = [];
      if (
        isAttemptedToFetchRedactedConversationError(action.payload) ||
        isCustomFetchErrorObject(action.payload)
      ) {
        state.error = action.payload;
      } else {
        state.error = action.error.message || null;
      }
    });
    builder.addCase(restartConversation.fulfilled, (state, { payload }) => {
      state.conversationStatus = 'success';
      state.loading = false;
      state.error = null;
      state.dismissableNotification = '';
      state.noUserActionsLeft = false;
      state.handoffStatus = 'not_started';
      state.didUserRestartConversation = true;
      state.windowUpdatedContextVariables = {};
      if (payload) {
        state.widgetComponents = payload.widget_components;
        state.conversationId = payload.conversation_id;
        state.transcriptLength = payload.transcript_length;
        state.nextStepId = payload.next_step_id;
      }
    });

    builder.addCase(setLogRocketInitialized, (state, action) => {
      const payload = action.payload;
      state.shouldInitLogRocket = payload;
    });
    builder.addCase(setIsWidgetVisible, (state, action) => {
      const isVisible = action.payload;
      if (isVisible) {
        state.handoffIntegration = null;
      }
    });
    builder.addCase(openWidget, state => {
      state.handoffIntegration = null;
    });
    builder.addCase(closeWidget, state => {
      state.handoffIntegration = null;
    });

    builder.addCase(resetIntentConversation.pending, state => {
      state.conversationStatus = 'start';
      state.loading = true;
      state.error = null;
      state.dismissableNotification = '';
      state.widgetComponents = [];
      state.previousIntentIds = [];
      state.triggerEvent = { ...initialState.triggerEvent };
      state.quickFeedback = initialState.quickFeedback;
    });

    builder.addCase(resetIntentConversation.fulfilled, (state, action) => {
      const selectIntentStep = getSelectIntentStep(
        (state.customConfigParams?.greeting_message as string | undefined) ||
          state.workflowsConfig?.greeting_line ||
          '',
      );
      state.conversationStatus = 'success';
      state.noUserActionsLeft = false;
      state.loading = false;
      state.didUserRestartConversation = true;
      state.widgetComponents = [selectIntentStep];
      state.selectedIntentIndex = null;
      state.transcriptLength = action.payload.transcript_length;
      state.nextStepId = action.payload.next_step_id;
      state.handoffStatus = 'not_started';
      state.windowUpdatedContextVariables = {};
      if (action.payload.modified_date) {
        state.modifiedDate = action.payload.modified_date;
        state.systemTimeDelta = getSystemClockDelta(
          action.payload.modified_date,
        );
      }
    });

    builder.addCase(resetIntentConversation.rejected, (state, action) => {
      state.conversationStatus = 'failure';
      state.loading = false;
      if (isCustomFetchErrorObject(action.payload)) {
        state.error = action.payload;
      } else {
        state.error = action.error.message || null;
      }
    });

    builder.addCase(resetPreviewWidgetConfig, (state, action) => {
      const shouldKeepConversationId = Boolean(action.payload);
      if (!shouldKeepConversationId) {
        state.conversationId = '';
      }
      state.conversationStatus = 'initial';
      state.workflowsConfig = null;
      state.widgetComponents = [];
      state.handoffStatus = 'not_started';
      state.triggerEvent = { ...initialState.triggerEvent };
      state.quickFeedback = initialState.quickFeedback;
    });

    builder.addCase(setDidUserCloseProactiveChat, (state, action) => {
      state.didUserCloseProactiveChat = action.payload;
    });

    builder.addCase(sendTrackingEvent.fulfilled, (state, { meta }) => {
      if (meta.arg.event_type === 'widget-customer-proactive-bubbles-shown') {
        state.didProactiveBubblesShownTrackingEventGetSent = true;
      }
    });

    builder.addCase(getTranslatedCsatText.pending, state => {
      state.loading = true;
    });

    builder.addCase(
      getPersistedLiveChatComponents.fulfilled,
      (state, action) => {
        const persistedLiveChatComponents = action.payload;
        state.persistedLiveChatComponents = persistedLiveChatComponents;
        if (persistedLiveChatComponents) {
          insertLiveChatWidgetComponents(state, persistedLiveChatComponents);
        }
      },
    );

    builder.addCase(getTranslatedCsatText.fulfilled, (state, action) => {
      state.loading = false;
      state.csatTranslatedText = action.payload;
    });

    builder.addCase(getTranslatedCsatText.rejected, (state, action) => {
      state.loading = false;
      if (
        isAttemptedToFetchRedactedConversationError(action.payload) ||
        isCustomFetchErrorObject(action.payload)
      ) {
        state.error = action.payload;
      } else {
        state.error = action.error.message || null;
      }
    });

    builder.addCase(setFreeFormQuery, (state, action) => {
      state.freeFormQuery = action.payload;

      if (!action.payload) {
        state.floatingBarSearchResult = undefined;
      }
    });

    builder.addCase(setConversationId, (state, action) => {
      state.conversationId = action.payload;
    });

    builder.addCase(sendFreeFormQuery.pending, (state, action) => {
      state.loading = true;
      state.error = null;
      state.dismissableNotification = '';
      state.freeFormQuery = '';

      // Hide CSAT survey:
      state.widgetComponents = state.widgetComponents.filter(
        component => component.component_type !== 'csat_survey',
      );
      state.noUserActionsLeft = false;

      const lastWidgetComponent =
        state.widgetComponents[state.widgetComponents.length - 1];

      if (lastWidgetComponent) {
        lastWidgetComponent.query = action.meta.arg;
        lastWidgetComponent.isSent = true;
      }
    });

    builder.addCase(
      sendFreeFormQuery.fulfilled,
      receiveUpdateConversationResponse,
    );

    // send feedback
    builder.addCase(sendConversationFeedback.pending, state => {
      const lastWidgetComponent =
        state.widgetComponents[state.widgetComponents.length - 1];

      // Show loading dots only when rerouted by negative feedback:
      if (lastWidgetComponent?.isReroutedByNegativeFeedback) {
        state.loading = true;
      }

      // made the call to resume, set to null
      if (
        state.csatStates.actionToExecuteAfterCsatDismiss ===
        'resume-from-trigger-point'
      ) {
        state.csatStates.actionToExecuteAfterCsatDismiss = null;
      }

      state.error = null;
      state.dismissableNotification = '';
    });
    builder.addCase(sendConversationFeedback.rejected, (state, action) => {
      state.loading = false;
      const payload = action.payload;
      if (
        isConversationNotFoundError(payload) ||
        isCustomFetchErrorObject(payload)
      ) {
        state.error = payload;
      } else {
        state.error = action.error.message || null;
      }
    });
    builder.addCase(sendConversationFeedback.fulfilled, (state, action) => {
      receiveUpdateConversationResponse(state, action);
    });

    builder.addCase(sendFreeFormQuery.rejected, (state, action) => {
      state.loading = false;

      const payload = action.payload;
      if (
        (isBadRequestError(payload) || isTooManyRequestsError(payload)) &&
        'isInteractiveEmail' in payload &&
        payload.isInteractiveEmail
      ) {
        state.widgetComponents =
          state.workflowsConfig?.initial_widget_components ?? [];
      } else if (isCustomFetchErrorObject(payload)) {
        state.error = payload;
      } else {
        state.error = action.error.message || null;
      }
    });

    builder.addCase(startNewIntentSelection.pending, (state, action) => {
      const lastWidgetComponent =
        state.widgetComponents[state.widgetComponents.length - 1];

      state.conversationStatus = 'start';
      state.loading = true;
      state.error = null;
      state.dismissableNotification = '';
      state.selectedIntentIndex = null;

      if (
        lastWidgetComponent &&
        (isFormComponent(lastWidgetComponent) ||
          isZendeskAttachmentUploadStepComponent(lastWidgetComponent))
      ) {
        lastWidgetComponent.isSent = true;
        lastWidgetComponent.neverMindSelection = action.meta.arg;
      }
    });

    builder.addCase(startNewIntentSelection.fulfilled, (state, action) => {
      const payload = action.payload;

      const selectIntentStep = getSelectIntentStep(
        (state.customConfigParams?.greeting_message as string | undefined) ||
          payload.greeting_line ||
          '',
      );
      if (payload && state.workflowsConfig !== null) {
        state.workflowsConfig.forethought_text = payload.forethought_text;
        state.workflowsConfig.top_n_intents = payload.top_n_intents;
      }

      state.conversationStatus = 'success';
      state.noUserActionsLeft = false;

      state.loading = false;
      state.widgetComponents = [...state.widgetComponents, selectIntentStep];
      state.transcriptLength = payload.restartData.transcript_length;
      state.nextStepId = payload.restartData.next_step_id;
      state.handoffStatus = 'not_started';
      state.conversationId = payload.conversation_id;
      state.triggerEvent = { ...initialState.triggerEvent };
      if (payload.restartData.modified_date) {
        state.modifiedDate = payload.restartData.modified_date;
        state.systemTimeDelta = getSystemClockDelta(
          payload.restartData.modified_date,
        );
      }
    });

    builder.addCase(startNewIntentSelection.rejected, (state, action) => {
      state.conversationStatus = 'failure';
      state.loading = false;
      if (
        isAttemptedToFetchRedactedConversationError(action.payload) ||
        isCustomFetchErrorObject(action.payload)
      ) {
        state.error = action.payload;
      } else {
        state.error = action.error.message || null;
      }
    });
    builder.addCase(getIntentsBySelectedTag.pending, state => {
      state.loading = true;
      state.error = null;
      state.dismissableNotification = '';
    });
    builder.addCase(getIntentsBySelectedTag.rejected, (state, action) => {
      state.loading = false;
      if (
        isAttemptedToFetchRedactedConversationError(action.payload) ||
        isCustomFetchErrorObject(action.payload)
      ) {
        state.error = action.payload;
      } else {
        state.error = action.error.message || null;
      }
    });
    builder.addCase(getIntentsBySelectedTag.fulfilled, (state, action) => {
      const selectIntentStep = getSelectIntentStep(
        action.payload.forethought_text?.tag_selection_greeting_message,
      );

      const payload = action.payload;
      const workflowParams = payload.workflow_params ?? {};

      state.workflowsConfig = filterWorkflowConfig(payload);
      state.loading = false;
      state.conversationId = payload.conversation_id;
      state.customParams = workflowParams;
      state.customConfigParams = payload.config_params;
      state.widgetComponents = [...state.widgetComponents, selectIntentStep];
    });

    builder.addCase(getAutoflowStream.pending, state => {
      state.streaming = true;
      state.error = null;
    });
    builder.addCase(getAutoflowStream.rejected, (state, action) => {
      state.streaming = false;
      if (isCustomFetchErrorObject(action.payload)) {
        state.error = action.payload;
      } else {
        state.error = action.error.message || null;
      }
    });
    builder.addCase(getAutoflowStream.fulfilled, state => {
      state.streaming = false;
    });

    builder.addCase(getKnowledgeRetrievalStream.pending, state => {
      state.streaming = true;
      state.error = null;
    });
    builder.addCase(getKnowledgeRetrievalStream.rejected, (state, action) => {
      state.streaming = false;
      if (isCustomFetchErrorObject(action.payload)) {
        state.error = action.payload;
      } else {
        state.error = action.error.message || null;
      }
    });
    builder.addCase(getKnowledgeRetrievalStream.fulfilled, state => {
      state.streaming = false;
    });

    builder.addCase(disablePersistence, state => {
      if (!state.workflowsConfig) {
        return;
      }

      state.workflowsConfig.persistence_enabled = false;
    });

    builder.addCase(navigateTo, (state, action) => {
      if (action.payload === ARTICLE_PAGE) {
        state.screenReaderNotifications.push('Article opened');
      }
    });
    builder.addCase(searchKnowledge.pending, (state, action) => {
      switch (action.meta.arg.origin) {
        case 'floating_bar':
          state.floatingBarSearchLoading = true;
          break;
        case 'search_page':
        default:
          state.widgetSearchLoading = true;
          break;
      }
    });
    builder.addCase(searchKnowledge.fulfilled, (state, action) => {
      switch (action.meta.arg.origin) {
        case 'floating_bar':
          state.floatingBarSearchLoading = false;

          // if no freeFormQuery dont show results
          if (state.freeFormQuery) {
            state.floatingBarSearchResult = action.payload;
          }
          break;
        case 'search_page':
        default:
          state.widgetSearchLoading = false;
          state.widgetSearchResult = action.payload;
          break;
      }
    });
    builder.addCase(searchKnowledge.rejected, (state, action) => {
      switch (action.meta.arg.origin) {
        case 'floating_bar':
          state.floatingBarSearchLoading = false;
          break;
        case 'search_page':
        default:
          state.widgetSearchLoading = false;
          break;
      }
    });
    builder.addCase(sendUserReply.fulfilled, (state, { payload }) => {
      state.modifiedDate = payload.timestamp;
      state.systemTimeDelta = getSystemClockDelta(payload.timestamp);
    });
    builder.addCase(sendAgentReply.fulfilled, (state, { payload }) => {
      state.modifiedDate = payload.timestamp;
      state.systemTimeDelta = getSystemClockDelta(payload.timestamp);
    });
    builder.addCase(
      updateForethoughtLiveChatHelpdeskConversationId.fulfilled,
      (state, { payload }) => {
        state.modifiedDate = payload.timestamp;
        state.systemTimeDelta = getSystemClockDelta(payload.timestamp);
      },
    );
    // For some reason screen readers start rereading old messages when widget
    // gets opened/closed.
    // Clear `screenReaderNotifications` to prevent that.
    builder.addMatcher(
      isAnyOf(setIsWidgetVisible, openWidget, closeWidget),
      state => {
        if (state.screenReaderNotifications.length > 0) {
          state.screenReaderNotifications = [];
        }
      },
    );
  },
  initialState,
  name: 'workflows',
  reducers: {
    addEventMessage: (state, action: PayloadAction<string>) => {
      const eventMessage = action.payload;
      if (eventMessage) {
        state.widgetComponents.push({
          component_fields: { message: eventMessage },
          component_type: 'event_message',
          isSent: true,
        });
      }
    },
    addScreenReaderNotification: (state, action: PayloadAction<string>) => {
      state.screenReaderNotifications.push(action.payload);
    },
    addWidgetComponents: (state, action: PayloadAction<WidgetComponent[]>) => {
      const updatedWidgetComponents = [
        ...state.widgetComponents,
        ...action.payload,
      ];
      // Filter specific widget component with transcript prefix
      state.widgetComponents = updatedWidgetComponents.filter(
        widgetComp =>
          !(
            isTextMessageComponent(widgetComp) &&
            widgetComp.component_fields?.message &&
            widgetComp.component_fields?.message.startsWith(
              PREFIX_TO_SKIP_RENDERING_WIDGET_COMPONENT,
            )
          ),
      );
    },
    botMessageReceived: state => {
      state.shouldFocusReceivedMessage = true;
      state.screenReaderNotifications.push(
        'You received a new message. Press "Tab" to focus it.',
      );
    },
    cancelMobileQuickFeedback(state) {
      state.quickFeedback.mobileClickedStepIndex = null;
    },
    changeAdditionalFeedback(
      state,
      action: PayloadAction<{ additionalFeedback: string; stepIndex: number }>,
    ) {
      const quickFeedbackStep =
        state.quickFeedback.steps[action.payload.stepIndex];

      if (!quickFeedbackStep) {
        return;
      }

      quickFeedbackStep.additionalFeedback = action.payload.additionalFeedback;
    },
    deleteForethoughtLiveChatWidgetComponents: state => {
      state.widgetComponents = state.widgetComponents.filter(
        widgetComponent =>
          !isForethoughtLiveChatHandoffComponent(widgetComponent) &&
          !isSuncoLiveChatComponent(widgetComponent),
      );
    },
    editQuickFeedback(state, action: PayloadAction<number>) {
      state.quickFeedback.mobileClickedStepIndex = null;

      const currentStep = state.quickFeedback.steps[action.payload];

      if (!currentStep) {
        return;
      }

      // If additional feedback is enabled, show the modal:
      if (
        currentStep.isUseful
          ? state.workflowsConfig?.quick_feedback_config
              .should_request_feedback_for_high_ratings
          : state.workflowsConfig?.quick_feedback_config
              .should_request_feedback_for_low_ratings
      ) {
        currentStep.isSent = false;
      }
    },
    finishDelayingNextMessage(state, action: PayloadAction<number>) {
      const delayingMessage = state.messageDelays.find(
        ({ status }) => status === 'delaying',
      );

      if (!delayingMessage) {
        return;
      }

      delayingMessage.status = 'delayed';
      delayingMessage.shownAtInMilliseconds = action.payload;
    },
    goToStage: (state, action: PayloadAction<number>) => {
      const nextStage = action.payload;
      const csatConfig = state.workflowsConfig?.csat_config;
      const currentStage = state.csatStates.currentStage;

      // stage can only move forward
      if (nextStage <= currentStage || !csatConfig) {
        return;
      }

      const {
        should_request_additional_feedback: shouldRequestAdditionalFeedback,
        should_request_feedback_for_high_ratings:
          shouldRequestFeedbackForHighRatings,
        should_request_feedback_for_low_ratings:
          shouldRequestFeedbackForLowRatings,
        should_request_resolution_confirmation:
          shouldRequestResolutionConfirmation,
      } = csatConfig;
      const isStageSkippable = [
        true,
        !shouldRequestFeedbackForHighRatings,
        !shouldRequestFeedbackForLowRatings,
        !shouldRequestResolutionConfirmation,
        !shouldRequestAdditionalFeedback,
        false,
      ];

      let finalStage;
      // if next stage doesn't need to be rendered, continue to move forward
      for (
        finalStage = nextStage;
        finalStage < isStageSkippable.length;
        finalStage++
      ) {
        if (!isStageSkippable[finalStage]) {
          break;
        }

        // move one more stage to get to resolution confirmation stage
        if (finalStage === HIGH_RATING_REASONS_STAGE) {
          finalStage = finalStage + 1;
        }
      }

      state.csatStates.currentStage = finalStage;
    },
    privacyConsented: state => {
      state.consentedPrivacyPolicyHash = hash(
        state.workflowsConfig?.privacy_consent_config?.privacy_policy || '',
      );
    },
    quickFeedbackClicked(
      state,
      action: PayloadAction<{ isUseful: boolean; stepIndex: number }>,
    ) {
      state.quickFeedback.mobileClickedStepIndex = null;

      const currentStep = state.quickFeedback.steps[action.payload.stepIndex];

      if (currentStep) {
        // If additional feedback is enabled, show the modal:
        if (
          action.payload.isUseful
            ? state.workflowsConfig?.quick_feedback_config
                .should_request_feedback_for_high_ratings
            : state.workflowsConfig?.quick_feedback_config
                .should_request_feedback_for_low_ratings
        ) {
          currentStep.isSent = false;
        }

        currentStep.isUseful = action.payload.isUseful;

        return;
      }

      // Step doesn't exist, create new one:
      state.quickFeedback.steps[action.payload.stepIndex] = {
        additionalFeedback: '',
        isSent: false,
        isUseful: action.payload.isUseful,
      };
    },
    quickFeedbackClickedMobile(state, action: PayloadAction<number>) {
      state.quickFeedback.mobileClickedStepIndex = action.payload;
    },
    quickFeedbackSent(state, action: PayloadAction<number>) {
      const quickFeedbackStep = state.quickFeedback.steps[action.payload];

      if (!quickFeedbackStep) {
        return;
      }

      quickFeedbackStep.isSent = true;
    },
    receiveStreamResponse: (
      state,
      action: PayloadAction<{
        isFirstResponse: boolean;
        receivedAtInMilliseconds: number;
        response: StreamingResponse;
      }>,
    ) => {
      const { isFirstResponse, receivedAtInMilliseconds, response } =
        action.payload;

      // Handle StreamingResponsePreviewLog case
      if (isPreviewLogStreamingResponse(response)) {
        if (state.isBuilderPreview || state.isSolveConfigPreview) {
          postForethoughtWidgetPreviewLogMessage(
            response.conversation_id,
            response.preview_logs,
          );
        }
        return;
      }

      // Handle StreamingResponseKR case
      const {
        modified_date,
        next_step_id,
        transcript_length,
        widget_components,
      } = response;

      if (transcript_length) {
        state.transcriptLength = transcript_length;
      }

      if (next_step_id) {
        state.nextStepId = next_step_id;
      }

      if (modified_date) {
        state.modifiedDate = modified_date;
        state.systemTimeDelta = getSystemClockDelta(modified_date);
      }

      const streamingWidgetComponents = widget_components.map(
        (widgetComponent, messageIndexInResponse) => {
          widgetComponent.isStreamingComponent = true;

          const timeSinceLastDelayInMilliseconds =
            receivedAtInMilliseconds -
            selectLastShownAtInMilliseconds({ workflows: state });

          if (
            selectIsDelaying({ workflows: state }) ||
            selectNextMessageToDelay({ workflows: state }) ||
            messageIndexInResponse > 0
          ) {
            widgetComponent.delay = ARTIFICIAL_DELAY_IN_MILLISECONDS;
          } else if (
            isFirstResponse ||
            timeSinceLastDelayInMilliseconds > ARTIFICIAL_DELAY_IN_MILLISECONDS
          ) {
            widgetComponent.delay = 0;
          } else {
            widgetComponent.delay =
              ARTIFICIAL_DELAY_IN_MILLISECONDS -
              timeSinceLastDelayInMilliseconds;
          }

          return widgetComponent;
        },
      );

      handleWidgetComponents(streamingWidgetComponents, state, false);
    },
    resetShouldFocusReceivedMessage: state => {
      state.shouldFocusReceivedMessage = false;
    },
    setActionToExecAfterPrivacyConsented(
      state,
      action: PayloadAction<
        WorkflowsState['actionToExecAfterPrivacyConsented']
      >,
    ) {
      state.actionToExecAfterPrivacyConsented = action.payload;
    },
    setCsatStatesByKey(
      state: WorkflowsState,
      action: PayloadAction<Partial<CsatStates>>,
    ) {
      Object.assign(state.csatStates, action.payload);
    },
    setDismissableNotification(state, action: PayloadAction<string>) {
      state.dismissableNotification = action.payload;
    },
    setFloatingBarSearchResult: (
      state,
      action: PayloadAction<KnowledgeSearchResponse | undefined>,
    ) => {
      state.floatingBarSearchResult = action.payload;
    },
    setForethoughtLiveChatStepSent: state => {
      state.widgetComponents = state.widgetComponents.map(widgetComponent => {
        if (
          isForethoughtLiveChatHandoffComponent(widgetComponent) ||
          isSuncoLiveChatComponent(widgetComponent)
        ) {
          widgetComponent.isSent = true;
        }
        return widgetComponent;
      });
    },
    setHandoffStatusAndErrorMessage: (
      state,
      action: PayloadAction<{
        handoffFailureMessage: string | undefined;
        handoffStatus: HandoffStatus;
      }>,
    ) => {
      state.handoffFailureMessage =
        action.payload.handoffFailureMessage || null;
      state.handoffStatus = action.payload.handoffStatus;
    },
    setInteractiveEmailDeflectionFeedback: (
      state,
      action: PayloadAction<
        WorkflowsState['interactiveEmailDeflectionFeedback']
      >,
    ) => {
      state.interactiveEmailDeflectionFeedback = action.payload;
    },
    setIsBrowserAgentInProgress: (state, action: PayloadAction<boolean>) => {
      state.isBrowserAgentInProgress = action.payload;
    },
    setIsFloatingBarFocused: (state, action: PayloadAction<boolean>) => {
      state.isFloatingBarFocused = action.payload;
    },
    setIsScrollAtTheBottom: (state, action: PayloadAction<boolean>) => {
      state.isScrollAtTheBottom = action.payload;
    },
    setIsUploadingAttachment: (state, action: PayloadAction<boolean>) => {
      state.isUploadingAttachment = action.payload;
    },
    setKnowledgeSearchQuery: (state, action: PayloadAction<string>) => {
      state.knowledgeSearchQuery = action.payload;
    },
    setLatestStepRerouted: state => {
      const lastWidgetComponent =
        state.widgetComponents[state.widgetComponents.length - 1];

      if (lastWidgetComponent) {
        lastWidgetComponent.isReroutedByNegativeFeedback = true;
        state.isScrollAtTheBottom = true;
      }
    },
    setShouldFocusTextInput: (state, action: PayloadAction<boolean>) => {
      state.shouldFocusTextInput = action.payload;
    },
    setSipCallStatesByKey(
      state: WorkflowsState,
      action: PayloadAction<Partial<SipCallState>>,
    ) {
      Object.assign(state.sipCallState, action.payload);
    },
    setStreaming: (state, action: PayloadAction<boolean>) => {
      state.streaming = action.payload;
    },
    setTouchedFormField: (state, action: PayloadAction<string>) => {
      if (!state.touchedFormFields.includes(action.payload)) {
        state.touchedFormFields.push(action.payload);
      }
    },
    setTouchedFormFields: (
      state,
      action: PayloadAction<WorkflowsState['touchedFormFields']>,
    ) => {
      state.touchedFormFields = action.payload;
    },
    setWidgetSearchResult: (
      state,
      action: PayloadAction<KnowledgeSearchResponse | undefined>,
    ) => {
      state.widgetSearchResult = action.payload;
    },
    setWindowUpdatedContextVariables(state, action: PayloadAction<unknown>) {
      const removedContextVariables: { key: unknown; value: unknown }[] = [];
      const isValidKey = (key: unknown) => {
        return typeof key === 'string' && validate(key.replace(/_/g, '-'));
      };
      const isValidValue = (value: unknown) => {
        return (
          typeof value === 'string' ||
          typeof value === 'number' ||
          typeof value === 'boolean' ||
          (Array.isArray(value) &&
            value.every(item => typeof item === 'string'))
        );
      };

      /**
       * Runtime type check because payload comes from end user
       */
      if (typeof action.payload !== 'object' || action.payload === null) {
        return;
      }

      /**
       * Filter out non uuid keys and non string, number, boolean values
       */
      const filteredPayload = Object.fromEntries(
        Object.entries(action.payload).filter(([key, value]) => {
          const isValid = isValidKey(key) && isValidValue(value);

          if (!isValid) {
            removedContextVariables.push({ key, value });
          }

          return isValid;
        }),
      );

      if (removedContextVariables.length > 0) {
        // eslint-disable-next-line no-console
        console.warn(
          `Failed to set the following context variable(s) due to them unsupported keys and/or values: ${JSON.stringify(removedContextVariables)}`,
        );
      }

      state.windowUpdatedContextVariables = {
        ...state.windowUpdatedContextVariables,
        ...filteredPayload,
      };
    },
    /**
     * Ignores `config-ft-disable-proactive-prompt` for the case when customer
     * wants proactive prompt to be hidden initially, but then wants to open it
     * programmatically.
     */
    showProactivePrompt: state => {
      delete state.customConfigParams?.disable_proactive_prompt;
      state.didUserCloseProactiveChat = false;
    },
    startDelayingNextMessage(state) {
      const nextMessageToDelay = selectNextMessageToDelay({ workflows: state });

      if (!nextMessageToDelay) {
        return;
      }

      nextMessageToDelay.status = 'delaying';
    },
    synchronizeMessageDelays: state => {
      state.messageDelays = state.widgetComponents.map(
        (widgetComponent, index) => {
          const isFirstMessageInResponse = index === state.messageDelays.length;
          const isEndInteractiveEmailChatStep =
            widgetComponent.component_type === 'end_interactive_email_chat';

          const delay = widgetComponent.isStreamingComponent
            ? widgetComponent.delay || 0
            : !isFirstMessageInResponse && !isEndInteractiveEmailChatStep
              ? ARTIFICIAL_DELAY_IN_MILLISECONDS
              : 0;

          return (
            state.messageDelays[index] || {
              delay,
              shownAtInMilliseconds: delay === 0 ? Date.now() : 0,
              status: 'not delayed',
            }
          );
        },
      );
    },
    updateConversationIdUsedInPreviousSession: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.conversationIdUsedInPreviousSession = action.payload;
    },
  },
});

export const {
  addEventMessage,
  addScreenReaderNotification,
  addWidgetComponents,
  botMessageReceived,
  cancelMobileQuickFeedback,
  changeAdditionalFeedback,
  deleteForethoughtLiveChatWidgetComponents,
  editQuickFeedback,
  finishDelayingNextMessage,
  goToStage,
  privacyConsented,
  quickFeedbackClicked,
  quickFeedbackClickedMobile,
  quickFeedbackSent,
  receiveStreamResponse,
  resetShouldFocusReceivedMessage,
  setActionToExecAfterPrivacyConsented,
  setCsatStatesByKey,
  setDismissableNotification,
  setFloatingBarSearchResult,
  setForethoughtLiveChatStepSent,
  setHandoffStatusAndErrorMessage,
  setInteractiveEmailDeflectionFeedback,
  setIsBrowserAgentInProgress,
  setIsFloatingBarFocused,
  setIsScrollAtTheBottom,
  setIsUploadingAttachment,
  setKnowledgeSearchQuery,
  setLatestStepRerouted,
  setShouldFocusTextInput,
  setSipCallStatesByKey,
  setStreaming,
  setTouchedFormField,
  setTouchedFormFields,
  setWindowUpdatedContextVariables,
  showProactivePrompt,
  startDelayingNextMessage,
  synchronizeMessageDelays,
  updateConversationIdUsedInPreviousSession,
} = workflowsSlice.actions;

export const selectConversationId = (
  state: RootState,
): WorkflowsState['conversationId'] => state.workflows?.conversationId;

export const selectNextStepId = (
  state: RootState,
): WorkflowsState['nextStepId'] => state.workflows?.nextStepId;

export const selectConversationStatus = (
  state: RootState,
): WorkflowsState['conversationStatus'] => state.workflows?.conversationStatus;

export const selectWidgetComponents = (
  state: RootState,
): WorkflowsState['widgetComponents'] => state.workflows.widgetComponents;

export const selectLoading = (state: RootState): WorkflowsState['loading'] =>
  state.workflows.loading || state.workflows.streaming;

export const selectError = (state: RootState): WorkflowsState['error'] =>
  state.workflows.error;

export const selectWorkflowsConfig = (
  state: RootState,
): WorkflowsState['workflowsConfig'] => state.workflows?.workflowsConfig;

const selectIsFreeFormIntentDetectionEnabled = (state: RootState): boolean =>
  Boolean(state.workflows?.workflowsConfig?.free_form_intent_detection_enabled);

export const selectCsatFormData = (
  state: RootState,
): WorkflowsState['csatFormData'] => state.workflows?.csatFormData;

export const selectCsatConfig = (
  state: RootState,
): WorkflowsState['csatConfig'] => state.workflows?.csatConfig;

export const selectCsatStates = (
  state: RootState,
): WorkflowsState['csatStates'] => state.workflows?.csatStates;

export const selectSipCallState = (
  state: RootState,
): WorkflowsState['sipCallState'] => state.workflows?.sipCallState;

const selectIsCsatHiddenDuringAgentChat = (state: RootState): boolean => {
  const shouldHideForethoughtCsatDuringAgentChat = selectFeatureFlags(
    state,
  ).includes('hide_forethought_csat_during_agent_chat');

  return (
    shouldHideForethoughtCsatDuringAgentChat && Boolean(state.liveChat.chatId)
  );
};

const selectIsCsatMinimizeDisabledDuringLiveChat = (state: RootState) => {
  const disabled = selectFeatureFlags(state).includes(
    'disable_csat_on_minimize_during_live_chat',
  );
  const isLiveChatMode = selectIsLiveChatMode(state);

  return disabled && isLiveChatMode;
};

const isShowWidgetOnMinimizeEnabled = (state: RootState): boolean => {
  const shouldShowCsat = Boolean(
    state.workflows.workflowsConfig?.csat_config?.show_when_widget_minimizes,
  );

  const numberOfUserInteractionRequired =
    state.workflows.workflowsConfig?.csat_config
      ?.show_csat_after_n_interactions ?? 0;

  const currentNumberOfUserInteractions = state.workflows.userInteractions ?? 0;

  return (
    shouldShowCsat &&
    currentNumberOfUserInteractions >= numberOfUserInteractionRequired
  );
};

export const selectIsCsatPromptableForMinimize = (
  state: RootState,
): boolean => {
  if (selectIsCsatHiddenDuringAgentChat(state)) {
    return false;
  }

  if (selectIsCsatMinimizeDisabledDuringLiveChat(state)) {
    return false;
  }

  return (
    selectFeatureFlags(state).includes('csat_config_revamp') &&
    Boolean(state.workflows.workflowsConfig?.csat_config?.is_enabled) &&
    isShowWidgetOnMinimizeEnabled(state) &&
    isValidConversationStatusForCsat(state.workflows?.conversationStatus) &&
    !state.workflows.csatStates.isCsatVisible &&
    !state.workflows.csatStates.hasDismissed &&
    !state.workflows.csatStates.hasSubmitted &&
    // csat can only be prompted if isPromptDisabledForMinimize is false or we're currently on one chat
    !(
      state.workflows.csatStates.isPromptDisabledForMinimize &&
      state.liveChat.status !== 'active'
    ) &&
    !state.liveChat.isEndLiveChatModalVisible
  );
};

export const selectIsCsatPromptableForEndLiveChat = (
  state: RootState,
): boolean => {
  const isCsatPromptable =
    selectFeatureFlags(state).includes('csat_config_revamp') &&
    Boolean(state.workflows.workflowsConfig?.csat_config?.is_enabled) &&
    Boolean(
      state.workflows.workflowsConfig?.csat_config?.show_when_agent_chat_ends,
    );

  if (selectIsCsatHiddenDuringAgentChat(state)) {
    return false;
  }

  const lastWidgetComponent = selectLastWidgetComponent(state);
  const lastWidgetComponentType = lastWidgetComponent?.component_type;

  return (
    isCsatPromptable &&
    //Don't surface CSAT if no agent has picked up a conversation yet
    lastWidgetComponentType !== 'forethought_live_chat_handoff'
  );
};

const selectIsCsatOnHeader = (state: RootState): boolean => {
  if (selectIsCsatHiddenDuringAgentChat(state)) {
    return false;
  }

  return (
    selectFeatureFlags(state).includes('csat_config_revamp') &&
    Boolean(state.workflows.workflowsConfig?.csat_config?.is_enabled) &&
    (Boolean(state.workflows.workflowsConfig?.csat_config?.show_on_header) ||
      state.workflows.csatStates.hasDismissed ||
      state.workflows.csatStates.hasSubmitted)
  );
};

export const selectIsFeedbackTooltipPromptable = (
  state: RootState,
): boolean => {
  const csatStates = state.workflows.csatStates;
  return (
    !csatStates.hasFeedbackTooltipPrompted &&
    !csatStates.isCsatVisible &&
    isValidConversationStatusForCsat(state.workflows?.conversationStatus)
  );
};

export const selectTranslatedCsatText = (
  state: RootState,
): WorkflowsState['csatTranslatedText'] => state.workflows?.csatTranslatedText;

export const selectIntentTitle = (
  state: RootState,
): WorkflowsState['intentTitle'] => state.workflows?.intentTitle;

export const selectIsModelTraining = (
  state: RootState,
): WorkflowsState['isModelTraining'] => state.workflows?.isModelTraining;

export const selectCustomConfigParams = (
  state: RootState,
): WorkflowsState['customConfigParams'] =>
  state.workflows?.customConfigParams ?? {};

export const selectHandoffStatus = (
  state: RootState,
): WorkflowsState['handoffStatus'] => state.workflows.handoffStatus;

export const selectHandoffFailureMessage = (
  state: RootState,
): WorkflowsState['handoffFailureMessage'] =>
  state.workflows.handoffFailureMessage;

export const selectSelectedArticle = (
  state: RootState,
): WorkflowsState['selectedArticle'] => state.workflows.selectedArticle;

export const selectIsBuilderPreview = (
  state: RootState,
): WorkflowsState['isBuilderPreview'] => state.workflows?.isBuilderPreview;

export const selectIsSolveConfigPreview = (
  state: RootState,
): WorkflowsState['isSolveConfigPreview'] =>
  state.workflows?.isSolveConfigPreview;

export const selectWorkflowVersion = (
  state: RootState,
): WorkflowsState['workflowVersion'] => state.workflows?.workflowVersion;

export const selectShouldInitLogRocket = (
  state: RootState,
): WorkflowsState['shouldInitLogRocket'] =>
  state.workflows?.shouldInitLogRocket;

export const selectSelectedIntentIndex = (
  state: RootState,
): WorkflowsState['selectedIntentIndex'] =>
  state.workflows?.selectedIntentIndex;

export const selectSessionId = (
  state: RootState,
): WorkflowsState['sessionId'] => state.workflows?.sessionId;

export const selectIsConversationStarted = (state: RootState) => {
  const { widgetComponents } = state.workflows || {};
  const isSelectingPersona = selectIsWorkflowTagSelectionActive(state);
  const isInitialQuery =
    widgetComponents.length === 1 &&
    widgetComponents[0]?.component_type === 'user_message' &&
    (
      widgetComponents[0]?.component_fields as {
        message: string;
      }
    )?.message === state.workflows.initialQuery;
  const isInitialInteractiveEmailState =
    widgetComponents.length === 1 &&
    widgetComponents[0]?.component_type === 'interactive_email_inquiry' &&
    widgetComponents[0]?.component_type ===
      state.workflows.workflowsConfig?.initial_widget_components?.[0]
        ?.component_type;

  return !(
    (widgetComponents.length === 1 &&
      widgetComponents[0]?.component_type === 'restart_conversation') ||
    isSelectingPersona ||
    isInitialQuery ||
    isInitialInteractiveEmailState
  );
};

const selectIsProactivePromptDisabled = (state: RootState): boolean => {
  const disableProactivePrompt =
    state.workflows?.customConfigParams?.disable_proactive_prompt;

  return (
    typeof disableProactivePrompt === 'string' &&
    disableProactivePrompt.toLowerCase() === 'true'
  );
};

export const selectIsProactiveChatVisible = (state: RootState): boolean => {
  const { didUserCloseProactiveChat, workflowsConfig } = state.workflows || {};

  const isConversationStarted = selectIsConversationStarted(state);
  const isProactivePromptDisabled = selectIsProactivePromptDisabled(state);
  const launchStyle = selectLaunchStyle(state);
  const isFloatingBarFocused = selectIsFloatingBarFocused(state);
  const searchResult = selectFloatingBarSearchResult(state);

  return Boolean(
    workflowsConfig?.display_proactive_chat &&
      !didUserCloseProactiveChat &&
      !isConversationStarted &&
      !isProactivePromptDisabled &&
      !(launchStyle === 'floating_bar' && !isFloatingBarFocused) &&
      !(
        launchStyle === 'floating_bar' &&
        !!searchResult &&
        searchResult.results.length > 0
      ),
  );
};

export const selectDidProactiveBubblesShownTrackingEventGetSent = (
  state: RootState,
): WorkflowsState['didProactiveBubblesShownTrackingEventGetSent'] =>
  state.workflows?.didProactiveBubblesShownTrackingEventGetSent;

export const selectDidUserRestartConversation = (
  state: RootState,
): WorkflowsState['didUserRestartConversation'] =>
  state.workflows?.didUserRestartConversation;

export const selectIntentIdToLoad = (
  state: RootState,
): string | null | undefined => state.workflows?.workflowsConfig?.intentId;

export const selectIntentId = (state: RootState): WorkflowsState['intentId'] =>
  state.workflows?.intentId;

export const selectIsAutonomousAgentWorkflowPreview = (
  state: RootState,
): WorkflowsState['isAutonomousAgentWorkflowPreview'] =>
  state.workflows?.isAutonomousAgentWorkflowPreview;

export const selectCsatSurveyVisibilityOnClose = (state: RootState): boolean =>
  Boolean(
    state.workflows?.workflowsConfig?.csat_survey_config?.is_enabled &&
      state.workflows?.numOfUserActions >=
        state.workflows?.workflowsConfig?.csat_survey_config
          ?.num_of_steps_required &&
      state.workflows?.conversationId &&
      !state.workflows?.csatConfig?.isCsatSurveyFinished &&
      !state.workflows?.isBuilderPreview &&
      !state.workflows?.isSolveConfigPreview &&
      !selectFeatureFlags(state).includes('csat_config_revamp'),
  );

export const selectCsatSurveyVisibilityOnWorkflow = (
  state: RootState,
): boolean =>
  Boolean(
    selectCsatSurveyVisibilityOnClose(state) &&
      state.workflows?.noUserActionsLeft,
  );

export const selectFreeFormQuery = (state: RootState): string =>
  state.workflows.freeFormQuery;

export const selectLastWidgetComponent = (
  state: RootState,
): WidgetComponent | undefined =>
  state.workflows.widgetComponents[state.workflows.widgetComponents.length - 1];

/**
 * Current logic only allows only one forethought live chat widget
 * component per conversation
 */
export const selectForethoughtLiveChatWidgetComponent = createSelector(
  [selectWidgetComponents],
  (widgetComponents): ForethoughtLiveChatHandoffComponent | undefined => {
    for (const wc of widgetComponents) {
      if (isForethoughtLiveChatHandoffComponent(wc)) {
        return wc;
      }
    }
  },
);

export const selectSalesforceLiveChatRoutingAttributes = createSelector(
  [selectForethoughtLiveChatWidgetComponent],
  (forethoughtLiveChatWidgetComponent): RoutingAttributes => {
    if (!forethoughtLiveChatWidgetComponent) {
      return {};
    }

    if (
      forethoughtLiveChatWidgetComponent.component_fields.integration_config
        .integration !== 'salesforce'
    ) {
      return {};
    }

    const visibleFields =
      forethoughtLiveChatWidgetComponent.component_fields.integration_config
        .integration_fields.visible_fields;

    const routingAttributes: RoutingAttributes = {
      ...forethoughtLiveChatWidgetComponent.component_fields.integration_config
        .integration_fields.hidden_fields,
    };

    // Extract only the value from visible_fields
    for (const [key, field] of Object.entries(visibleFields)) {
      routingAttributes[key] = field.value;
    }

    return routingAttributes;
  },
);

export const selectSuncoLiveChatWidgetComponent = createSelector(
  [selectWidgetComponents],
  (widgetComponents): SuncoLiveChatComponent | undefined => {
    for (const wc of widgetComponents) {
      if (isSuncoLiveChatComponent(wc)) {
        return wc;
      }
    }
  },
);

export const selectIsWorkflowTagSelectionActive = (
  state: RootState,
): boolean | undefined => {
  const widgetComponents = state.workflows.widgetComponents;

  /**
   * If the tag selection is enabled we will always have a message step in position 0
   * and the tag selector in position 1
   */
  const isWorkflowTagSelector =
    widgetComponents[1] && isWorkflowTagSelectorComponent(widgetComponents[1]);

  /**
   * widgetComponents.length <= 3 means the user hasn't started a conversation yet
   * because we will have a text message step, a button step to select a tag and
   * the intent step selector with no intents selected. So if the conversation hasn't
   * started we should still see the proactive bubbles when closing the widget
   */
  return isWorkflowTagSelector && widgetComponents.length <= 3;
};

const defaultWorkflowTags: WorkflowConfigArgs['workflow_tags'] = [];

export const selectWorkflowTags = (state: RootState): Array<string> =>
  state.workflows.workflowsConfig?.workflow_tags || defaultWorkflowTags;

export const selectTriggerEvent = (
  state: RootState,
): WorkflowsState['triggerEvent'] => state.workflows.triggerEvent;

export const selectInitialIntentId = (state: RootState): string =>
  state.workflows.initialIntentId;

export const selectInitialQuery = (state: RootState): string =>
  state.workflows.initialQuery;

export const selectIsWidgetEnabled = (state: RootState): boolean => {
  return (
    state.workflows.widget_enabled &&
    !isForethoughtDisabledByLocalStorage(state.config.apiKey)
  );
};

export const selectWidgetMode = (state: RootState): WidgetMode => {
  return selectLiveChatWidgetComponentExists(state) ? 'liveChat' : 'workflow';
};

export const selectIsLiveChatMode = (state: RootState): boolean => {
  return selectWidgetMode(state) === 'liveChat';
};

export const selectIsActiveLiveChat = (state: RootState): boolean =>
  selectIsLiveChatMode(state) && state.liveChat.status === 'active';

export const selectScreenReaderNotifications = (
  state: RootState,
): WorkflowsState['screenReaderNotifications'] =>
  state.workflows.screenReaderNotifications;

export const selectShouldFocusReceivedMessage = (
  state: RootState,
): WorkflowsState['shouldFocusReceivedMessage'] =>
  state.workflows.shouldFocusReceivedMessage;

export const selectVisibleWidgetComponentCount = (state: RootState): number =>
  state.workflows.widgetComponents.filter(
    widgetComponent =>
      !invisibleComponentTypes.includes(widgetComponent.component_type),
  ).length;

const defaultFeatureFlags: WorkflowsState['enabled_feature_flags'] = [];

export const selectFeatureFlags = (
  state: RootState,
): WorkflowsState['enabled_feature_flags'] =>
  state.workflows.enabled_feature_flags || defaultFeatureFlags;

const selectIsTranscriptCheckpointDataInvalid = (state: RootState): boolean =>
  !state.workflows.nextStepId && state.workflows.transcriptLength === null;

export const selectDismissableNotification = (
  state: RootState,
): WorkflowsState['dismissableNotification'] =>
  state.workflows.dismissableNotification;

export const selectIsUploadingAttachment = (
  state: RootState,
): WorkflowsState['isUploadingAttachment'] =>
  state.workflows.isUploadingAttachment;

export const selectIsCsatTextEmptyFromResponse = (
  state: RootState,
): boolean => {
  const translatedCsatText = state.workflows.csatTranslatedText;
  const isCsatCurrentlyShown = state.workflows.csatConfig.isCsatVisible;

  // If CSAT render flag is true, check if response is returned with no data
  if (isCsatCurrentlyShown) {
    return (
      !translatedCsatText ||
      (translatedCsatText && isObjectEmpty(translatedCsatText))
    );
  }
  // If CSAT is currently not shown, translatedCsatText can be null (did not fetch yet)
  return (translatedCsatText && isObjectEmpty(translatedCsatText)) ?? false;
};

export const selectConversationIdUsedInPreviousSession = (
  state: RootState,
): WorkflowsState['conversationIdUsedInPreviousSession'] =>
  state.workflows.conversationIdUsedInPreviousSession;

const selectLastVisibleMessageIndex = (state: RootState): number =>
  state.workflows.messageDelays.findLastIndex(
    ({ delay, status }) => delay === 0 || status === 'delayed',
  );

export const selectVisibleWidgetComponents = createSelector(
  [selectWidgetComponents, selectLastVisibleMessageIndex],
  (
    widgetComponents,
    lastVisibleMessageIndex,
  ): WorkflowsState['widgetComponents'] =>
    widgetComponents.slice(0, lastVisibleMessageIndex + 1),
);

export const selectIsDelaying = (
  state: Pick<RootState, 'workflows'>,
): boolean =>
  state.workflows.messageDelays.some(({ status }) => status === 'delaying');

export const selectDelayingMessage = (
  state: Pick<RootState, 'workflows'>,
): WorkflowsState['messageDelays'][number] | undefined =>
  state.workflows.messageDelays.find(({ status }) => status === 'delaying');

export const selectNextMessageToDelay = (
  state: Pick<RootState, 'workflows'>,
): WorkflowsState['messageDelays'][number] | undefined =>
  state.workflows.messageDelays.find(
    ({ delay, status }) => delay > 0 && status === 'not delayed',
  );

export const selectShouldSynchronizeMessageDelays = (
  state: RootState,
): boolean =>
  state.workflows.widgetComponents.length !==
  state.workflows.messageDelays.length;

const selectLastShownAtInMilliseconds = (
  state: Pick<RootState, 'workflows'>,
): number =>
  state.workflows.messageDelays[state.workflows.messageDelays.length - 1]
    ?.shownAtInMilliseconds || 0;

export const selectTouchedFormFields = (
  state: RootState,
): WorkflowsState['touchedFormFields'] => state.workflows.touchedFormFields;

export const selectIsScrollAtTheBottom = (
  state: RootState,
): WorkflowsState['isScrollAtTheBottom'] => state.workflows.isScrollAtTheBottom;

export const selectIsBrowserAgentLoading = (
  state: RootState,
): WorkflowsState['isBrowserAgentInProgress'] =>
  state.workflows.isBrowserAgentInProgress;

export const selectAreLoadingDotsVisible = createSelector(
  [selectWidgetMode, selectLoading, selectIsDelaying],
  (widgetMode, loading, isDelaying): boolean =>
    widgetMode === 'workflow' && (loading || isDelaying),
);

const selectAreAnyLoadingDotsVisible = createSelector(
  [selectAreLoadingDotsVisible, selectAreAgentTypingDotsVisible],
  (areLoadingDotsVisible, areAgentTypingDotsVisible): boolean =>
    areLoadingDotsVisible || areAgentTypingDotsVisible,
);

export const selectQuickFeedbackConfig = (
  state: RootState,
): WorkflowConfigArgs['quick_feedback_config'] | undefined =>
  state.workflows.workflowsConfig?.quick_feedback_config;

export const selectQuickFeedbackSteps = (
  state: RootState,
): WorkflowsState['quickFeedback']['steps'] =>
  state.workflows.quickFeedback.steps;

export const selectQuickFeedbackMobileClickedStepIndex = (
  state: RootState,
): WorkflowsState['quickFeedback']['mobileClickedStepIndex'] =>
  state.workflows.quickFeedback.mobileClickedStepIndex;

export const selectIsLatestStepZendeskCsat = (state: RootState): boolean => {
  const latestWidgetComponent =
    state.workflows.widgetComponents[
      state.workflows.widgetComponents.length - 1
    ];

  if (!latestWidgetComponent) {
    return false;
  }

  return Boolean(
    (latestWidgetComponent as UserMessageComponent).component_fields
      .csat_survey_link,
  );
};

export const selectPersistenceDurationMilliseconds = (
  state: PersistedState,
): number =>
  (state?.workflows?.workflowsConfig?.persistence_duration_minutes ||
    defaultPersistenceDurationMinutes) *
  60 *
  1000;

export const selectUpdatedAtMilliseconds = (state: PersistedState): number => {
  if (state?.workflows?.modifiedDate) {
    return parseDateToUtcTimestamp(state.workflows.modifiedDate);
  }

  return state?.config?.updatedAtMilliseconds || 0;
};

export const selectIsMobileSDK = (state: RootState): boolean =>
  state.workflows.customConfigParams?.mobile_sdk === 'true';

export const selectPersistedLiveChatComponents = (
  state: RootState,
): WorkflowsState['persistedLiveChatComponents'] =>
  state.workflows.persistedLiveChatComponents;

export const selectMessageBubblePositions = createSelector(
  [selectWidgetComponents],
  getMessageBubblePositions,
);

export const selectCsatUiState = createSelector(
  [
    selectLoading,
    selectIsCsatOnHeader,
    selectIsTranscriptCheckpointDataInvalid,
    selectConversationStatus,
    selectCsatStates,
  ],
  (
    isLoading,
    isCsatOnHeader,
    isTranscriptCheckpointDataInvalid,
    conversationStatus,
    csatStates,
  ) => {
    const disabled =
      isLoading ||
      isTranscriptCheckpointDataInvalid ||
      !isValidConversationStatusForCsat(conversationStatus);
    const show = isCsatOnHeader;
    const visible = csatStates.isCsatVisible;

    return {
      disabled,
      show,
      visible,
    };
  },
);

const selectForethoughtText = createSelector(
  [selectWorkflowsConfig],
  workflowsConfig => {
    if (workflowsConfig) {
      return workflowsConfig.forethought_text;
    }
  },
);

export const selectFreeFormInputPlaceholder = createSelector(
  [selectConversationStatus, selectForethoughtText, selectWidgetComponents],
  (conversationStatus, forethoughtText, widgetComponents) => {
    if (!forethoughtText) {
      return 'Type here...';
    }

    switch (conversationStatus) {
      case 'initial':
        return `${forethoughtText.free_form_input_placeholder_not_started}...`;
      default:
        if (widgetComponents.length === 1) {
          return `${forethoughtText.free_form_input_placeholder_not_started}...`;
        } else {
          return `${forethoughtText.free_form_input_placeholder_in_progress}...`;
        }
    }
  },
);

export const selectSourcesText = createSelector(
  [selectForethoughtText, selectCustomConfigParams],
  (forethoughtText, customConfigParams) => {
    return getOverridableConfigValue({
      customConfigValue: customConfigParams?.sources_text as string,
      defaultValue: 'Sources',
      widgetConfigValue: forethoughtText?.sources,
    });
  },
);

export const selectIsAuthorIconVisible = createSelector(
  [
    selectAreAnyLoadingDotsVisible,
    selectVisibleWidgetComponents,
    (state: RootState, stepIndex: number) => stepIndex,
  ],
  (areAnyLoadingDotsVisible, visibleWidgetComponents, stepIndex): boolean => {
    const isLastMessage =
      !areAnyLoadingDotsVisible &&
      visibleWidgetComponents.findLastIndex(
        widgetComponent =>
          !invisibleComponentTypes.includes(widgetComponent.component_type) &&
          widgetComponent.component_type !== 'user_message' &&
          widgetComponent.component_type !== 'event_message',
      ) === stepIndex;

    const isBeforeForethoughtLiveChatHandoff = () => {
      const forethoughtLiveChatIndex = visibleWidgetComponents.findIndex(
        widgetComponent =>
          widgetComponent.component_type === 'forethought_live_chat_handoff',
      );

      if (forethoughtLiveChatIndex === -1) {
        return false;
      } else {
        return (
          visibleWidgetComponents
            .slice(0, forethoughtLiveChatIndex)
            .findLastIndex(
              widgetComponent =>
                !invisibleComponentTypes.includes(
                  widgetComponent.component_type,
                ) && widgetComponent.component_type !== 'user_message',
            ) === stepIndex
        );
      }
    };

    return isLastMessage || isBeforeForethoughtLiveChatHandoff();
  },
);

export const selectBrowserAgentLoadingTexts = createSelector(
  [selectWidgetComponents],
  (widgetComponents): string[] => {
    if (!Array.isArray(widgetComponents)) return LOADING_STATIC_TEXT;

    const browserAgentComponent = widgetComponents.find(
      wc => wc?.component_type === 'browser_agent',
    ) as BrowserAgentComponent | undefined;

    const loadingTexts = browserAgentComponent?.component_fields?.loading_texts;
    if (Array.isArray(loadingTexts) && loadingTexts.length > 0) {
      return loadingTexts;
    }
    return LOADING_STATIC_TEXT;
  },
);

export const selectIsFloatingBarFocused = (state: RootState): boolean =>
  state.workflows.isFloatingBarFocused;

export const selectIsFloatingBarExpanded = (state: RootState): boolean => {
  return (
    state.workflows.isFloatingBarFocused ||
    Boolean(state.workflows.freeFormQuery) ||
    Boolean(state.workflows.floatingBarSearchResult?.results.length)
  );
};

export const selectIsRestartDisabled = (state: RootState): boolean => {
  const conversationStatus = selectConversationStatus(state);
  const loading = selectLoading(state);
  const isTranscriptCheckpointDataInvalid =
    selectIsTranscriptCheckpointDataInvalid(state);
  const error = selectError(state);

  return (
    conversationStatus === 'initial' ||
    conversationStatus === 'start' ||
    loading ||
    isTranscriptCheckpointDataInvalid ||
    isConversationNotFoundError(error)
  );
};

export const selectIsConversationActive = (state: RootState): boolean => {
  const conversationStatus = selectConversationStatus(state);
  const widgetComponents = selectWidgetComponents(state);

  return conversationStatus === 'success' && widgetComponents.length > 1;
};

export const selectShouldFocusTextInput = (
  state: RootState,
): WorkflowsState['shouldFocusTextInput'] =>
  state.workflows.shouldFocusTextInput;

export const selectWindowUpdatedContextVariables = (
  state: RootState,
): ContextVariables => state.workflows.windowUpdatedContextVariables;

export const selectProactiveTriggers = (
  state: RootState,
): WorkflowsState['proactiveTriggers'] => state.workflows.proactiveTriggers;

export const selectFloatingBarSearchResult = (
  state: RootState,
): WorkflowsState['floatingBarSearchResult'] =>
  state.workflows.floatingBarSearchResult;

export const selectWidgetSearchResult = (
  state: RootState,
): WorkflowsState['widgetSearchResult'] => state.workflows.widgetSearchResult;

export const selectKnowledgeSearchQuery = (
  state: RootState,
): WorkflowsState['knowledgeSearchQuery'] =>
  state.workflows.knowledgeSearchQuery;

export const selectWidgetSearchLoading = (
  state: RootState,
): WorkflowsState['widgetSearchLoading'] => state.workflows.widgetSearchLoading;

export const selectFloatingBarSearchLoading = (
  state: RootState,
): WorkflowsState['floatingBarSearchLoading'] =>
  state.workflows.floatingBarSearchLoading;

export const selectInteractiveEmailDeflectionFeedback = (
  state: RootState,
): WorkflowsState['interactiveEmailDeflectionFeedback'] =>
  state.workflows.interactiveEmailDeflectionFeedback;

export const selectHasBeenInteractedWith = (state: RootState): boolean => {
  return state.workflows.widgetComponents.some(
    widgetComponent =>
      widgetComponent.component_type === 'text_message' &&
      widgetComponent.isSent,
  );
};

const selectIsSubmitDisabledBySignInStep = (state: RootState): boolean => {
  const lastWidgetComponent = selectLastWidgetComponent(state);

  // check if input should be disabled from sign in step
  if (lastWidgetComponent && isOauthSignInComponent(lastWidgetComponent)) {
    // check if its disabled and contextVariables doesn't have a status
    if (
      lastWidgetComponent.component_fields.disable_other_inputs &&
      !lastWidgetComponent.contextVariables?.status
    ) {
      return true;
    }
  }

  return false;
};

export const selectIsFreeFormInputDisabled = (state: RootState): boolean => {
  const freeFormQuery = selectFreeFormQuery(state);
  const isLoading = selectLoading(state);

  return Boolean(!freeFormQuery || !freeFormQuery.trim() || isLoading);
};

export const selectIsFreeFormInputVisible = (state: RootState): boolean => {
  const isFreeFormIntentDetectionEnabled =
    selectIsFreeFormIntentDetectionEnabled(state);
  const error = selectError(state);
  const isPreview = selectIsBuilderPreview(state);
  const isDraft = selectIsDraft(state);
  const areLoadingDotsVisible = selectAreLoadingDotsVisible(state);
  const lastWidgetComponent = selectLastWidgetComponent(state);
  const isSubmitDisabledBySignInStep =
    selectIsSubmitDisabledBySignInStep(state);
  const isAutonomousAgent = selectIsAutonomousAgent(state);

  const lastComponentType = lastWidgetComponent?.component_type;
  const componentsWithNeverMindButton: ComponentTypes[] = [
    'form',
    'zendesk_attachment_upload',
  ];

  return (
    isFreeFormIntentDetectionEnabled &&
    // Do not show free-form input if there is an error:
    !error &&
    // If preview mode, show free-form input only on landing page:
    (isPreview ? !isDraft : true) &&
    (areLoadingDotsVisible ||
      // components with "Never mind" button should not have free-form input unless they are sent:
      (!(
        !lastWidgetComponent?.isSent &&
        lastComponentType &&
        componentsWithNeverMindButton.includes(lastComponentType)
      ) &&
        // workflow tag selector should not have free-form input:
        !(
          lastWidgetComponent &&
          isWorkflowTagSelectorComponent(lastWidgetComponent)
        ))) &&
    // Do not show free-form input after end_interactive_email_chat:
    lastComponentType !== 'end_interactive_email_chat' &&
    !(
      lastWidgetComponent &&
      isButtonStepWithHideFreeFormQueryField(lastWidgetComponent)
    ) &&
    // Do not show free-form input during sign in step with disable_other_inptus
    !isSubmitDisabledBySignInStep &&
    !isAutonomousAgent
  );
};

/**
 * Returns the systemTimeDelta unless selectUpdatedAtMilliseconds return 0
 * in which case this also returns 0 to not mess with the persistence
 * calculations.
 */
export const selectSystemTimeDelta = (state: PersistedState): number => {
  const updatedAtMilliseconds = selectUpdatedAtMilliseconds(state);

  if (!updatedAtMilliseconds) {
    return 0;
  }

  return state.workflows?.systemTimeDelta || 0;
};

export const selectLastProactiveTriggerId = (state: RootState): string => {
  return state.workflows.lastProactiveTriggerId || '';
};
